import type { SourceId } from '../../../constants';
import { SourceTypes } from '../../../constants';

type NestedKeyOf<T, K = keyof T> = K extends keyof T & (string | number)
  ?
      | `${K}`
      | (T[K] extends Record<string, unknown>
          ? `${K}.${NestedKeyOf<T[K]>}`
          : never)
  : never;

export type StorageResponseType = {
  error?: {
    code: number;
    message: string;
  };
  success?: boolean;
};

export type DataServiceApiResponse<T = unknown> = {
  code: 200 | 201 | 202 | 204 | 400 | 401 | 403 | 404 | 409 | 422 | 429 | 500;
  message: string;
  payload: T;
};

export type ResponseType = StorageResponseType | DataServiceApiResponse;

export function getResponseError(response: ResponseType, platform: SourceId) {
  if (!response) {
    return 'Empty response';
  }

  if ('error' in response && response?.error?.message) {
    return response.error.message;
  }

  if (
    'success' in response &&
    platform === SourceTypes.TIKTOK &&
    !response?.success
  ) {
    return 'Some error occurred';
  }

  if (
    'message' in response &&
    platform !== SourceTypes.PINTEREST &&
    response?.message
  ) {
    return response.message;
  }

  return undefined;
}

export function handleResponseError<T extends ResponseType>(
  response: T,
  platform: SourceId,
  requiredItemsEntity?: NestedKeyOf<T>
) {
  const error = getResponseError(response, platform);

  if (error) {
    throw new Error(error);
  }

  if (
    requiredItemsEntity &&
    !hasNestedProperty(response, requiredItemsEntity)
  ) {
    throw new Error('Empty items response');
  }
}

function hasNestedProperty(obj: unknown, path: string) {
  const keys = path.split('.');
  let current = obj;

  for (const key of keys) {
    if (
      current == null ||
      typeof current !== 'object' ||
      !Object.hasOwn(current, key)
    ) {
      return false;
    }
    current = (current as Record<string, unknown>)[key];
  }

  return true;
}
